export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s-()]+$/;
  return phoneRegex.test(phone);
};

export const validatePostalCode = (postalCode: string): boolean => {
  // Canadian postal code format: A1A 1A1
  const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
  return postalCodeRegex.test(postalCode);
};

export const validateUsernameFormat = (username: string): boolean => {
  // 4-20 characters, no @ symbol
  return username.length >= 4 && username.length <= 20 && !username.includes('@');
};

export interface ValidationError {
  field: string;
  message: string;
}

export const validateAccountInfo = (data: Record<string, any>): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (data.professionalEmail && !validateEmail(data.professionalEmail)) {
    errors.push({ field: 'professionalEmail', message: 'Invalid professional email format' });
  }

  if (data.personalEmail && !validateEmail(data.personalEmail)) {
    errors.push({ field: 'personalEmail', message: 'Invalid personal email format' });
  }

  return errors;
};

export const validateCompanyInfo = (data: Record<string, any>): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!data.companyName?.trim()) {
    errors.push({ field: 'companyName', message: 'Company name is required' });
  }

  if (!data.professionalPhone?.trim()) {
    errors.push({ field: 'professionalPhone', message: 'Phone number is required' });
  } else if (!validatePhoneNumber(data.professionalPhone)) {
    errors.push({ field: 'professionalPhone', message: 'Invalid phone number format' });
  }

  if (!data.address?.trim()) {
    errors.push({ field: 'address', message: 'Address is required' });
  }

  if (!data.postalCode?.trim()) {
    errors.push({ field: 'postalCode', message: 'Postal code is required' });
  } else if (!validatePostalCode(data.postalCode)) {
    errors.push({ field: 'postalCode', message: 'Invalid postal code format (e.g. A1A 1A1)' });
  }

  if (!data.city?.trim()) {
    errors.push({ field: 'city', message: 'City is required' });
  }

  if (!data.province?.trim()) {
    errors.push({ field: 'province', message: 'Province is required' });
  }

  return errors;
};

export const validateDealerInfo = (data: Record<string, any>): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!data.dealerCompanyId) {
    errors.push({ field: 'dealerCompanyId', message: 'Primary dealer is required' });
  }

  if (!data.dealerRepresentativeId) {
    errors.push({ field: 'dealerRepresentativeId', message: 'Sales representative is required' });
  }

  return errors;
};

export const validatePasswordData = (data: Record<string, any>): ValidationError[] => {
  const errors: ValidationError[] = [];
  const passwordRegex = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;

  if (!data.currentPassword) {
    errors.push({
      field: 'currentPassword',
      message: 'Current password is required'
    });
  }

  if (!data.newPassword) {
    errors.push({
      field: 'newPassword',
      message: 'New password is required'
    });
  } else if (!passwordRegex.test(data.newPassword)) {
    errors.push({
      field: 'newPassword',
      message: 'Password must contain at least 8 characters, 1 capital letter, 1 symbol and 1 number'
    });
  }

  if (!data.confirmPassword) {
    errors.push({
      field: 'confirmPassword',
      message: 'Confirm password is required'
    });
  } else if (data.newPassword !== data.confirmPassword) {
    errors.push({
      field: 'confirmPassword',
      message: 'Passwords do not match'
    });
  }

  return errors;
};

export const validateUsernameData = (data: Record<string, any>): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!data.newUsername?.trim()) {
    errors.push({ field: 'newUsername', message: 'New username is required' });
  } else if (!validateUsernameFormat(data.newUsername)) {
    errors.push({ field: 'newUsername', message: 'Username must be 4-20 characters and not contain @' });
  }

  return errors;
};
